<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  DashboardPeriodOptions,
  IDashboardTopNgrItemsData,
  IDashboardTopPlayersItemsData,
  IDashboardTopTrackersItemsData,
} from '@modules/dashboard/types';
import type { ITableData } from '@modules/tables/components/VTable/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDropdownMenu from '@components/dropdowns/VDropdownMenu/VDropdownMenu.vue';
import VLoader from '@components/loader/VLoader.vue';
import {
  useDashboardFilters,
  useDashboardPeriodOptions,
} from '@modules/dashboard/composables';
import {
  DASHBOARD_PERIOD_OPTION_DEFAULT,
  TRANSLATION_GROUP_DASHBOARD_ADMIN,
} from '@modules/dashboard/constants';
import { EDashboardTopsModelTypes } from '@modules/dashboard/enums';
import { useDashboardTopsQuery } from '@modules/dashboard/queries';
import { ADMIN_REPORTS_ROUTE_NAME, REPORTS_PERMISSIONS } from '@modules/reports/constants';
import VTable from '@modules/tables/components/VTable/VTable.vue';

const { merchantId } = useDashboardFilters();

const {
  data: dashboardTops,
  isLoading,
} = useDashboardTopsQuery(merchantId);
const { periodOptions } = useDashboardPeriodOptions();

const ngrOption = ref<DashboardPeriodOptions>(DASHBOARD_PERIOD_OPTION_DEFAULT);
const trackersOption = ref<DashboardPeriodOptions>(DASHBOARD_PERIOD_OPTION_DEFAULT);
const playersOption = ref<DashboardPeriodOptions>(DASHBOARD_PERIOD_OPTION_DEFAULT);

const columnOptionsNgr: ITableColumnsOptions = {
  affiliate_username: {
    defaultMobilePreview: true,
  },
};

const columnOptionsTrackers: ITableColumnsOptions = {
  serial_id: {
    defaultMobilePreview: true,
  },
};

const columnOptionsPlayers: ITableColumnsOptions = {
  cid: {
    defaultMobilePreview: true,
  },
};

function setNgrOption(value: DashboardPeriodOptions): void {
  ngrOption.value = value;
}

function setTrackersOption(value: DashboardPeriodOptions): void {
  trackersOption.value = value;
}

function setPlayersOption(value: DashboardPeriodOptions): void {
  playersOption.value = value;
}

const topsNgrData = computed<ITableData<IDashboardTopNgrItemsData>>(() => ({
  list: dashboardTops.value?.[EDashboardTopsModelTypes.NGR][ngrOption.value] ?? [],
  columns_for_display_data: dashboardTops.value?.columns_for_display_data[EDashboardTopsModelTypes.NGR] ?? [],
  locale_key: `${dashboardTops.value?.locale_key}.${[EDashboardTopsModelTypes.NGR]}`,
}));

const topsPlayersData = computed<ITableData<IDashboardTopPlayersItemsData>>(() => ({
  list: dashboardTops.value?.[EDashboardTopsModelTypes.PLAYERS][playersOption.value] ?? [],
  columns_for_display_data: dashboardTops.value?.columns_for_display_data[EDashboardTopsModelTypes.PLAYERS] ?? [],
  locale_key: `${dashboardTops.value?.locale_key}.${[EDashboardTopsModelTypes.PLAYERS]}`,
}));

const topsTrackersData = computed<ITableData<IDashboardTopTrackersItemsData>>(() => ({
  list:
      dashboardTops.value?.[EDashboardTopsModelTypes.TRACKERS][trackersOption.value] ?? [],
  columns_for_display_data: dashboardTops.value?.columns_for_display_data[EDashboardTopsModelTypes.TRACKERS] ?? [],
  locale_key: `${dashboardTops.value?.locale_key}.${[EDashboardTopsModelTypes.TRACKERS]}`,
}));
</script>

<template>
  <div v-data-qa-auto-id="'TheDashboardTops'">
    <div class="flex flex-col gap-3">
      <VCard
        v-if="topsNgrData"
        type="table"
      >
        <VLoader
          :loading="isLoading"
          mode="overlay"
        >
          <VTable
            :data="topsNgrData"
            disable-pagination
            disable-sort
            :refresh-button="false"
            :title-t-path="$getTPath(TRANSLATION_GROUP_DASHBOARD_ADMIN, 'TOP_PARTNERS_HEADER')"
            :columns-options="columnOptionsNgr"
          >
            <template #searchFilter>
              <VDropdownMenu
                name="top_for_ngr"
                :options="periodOptions"
                underlined
                style-type="dark"
                :value="ngrOption"
                @change="setNgrOption"
              />
            </template>
            <template
              v-if="$hasPermission(REPORTS_PERMISSIONS.REPORTS_GENERATE_REPORT)"
              #footerButtons
            >
              <VButton
                :to="{ name: ADMIN_REPORTS_ROUTE_NAME }"
                :label="$getTranslation(TRANSLATION_GROUP_DASHBOARD_ADMIN, 'BUTTON_REPORTS')"
              />
            </template>
          </VTable>
        </VLoader>
      </VCard>
      <VCard
        v-if="topsTrackersData"
        type="table"
      >
        <VLoader
          :loading="isLoading"
          mode="overlay"
        >
          <VTable
            :data="topsTrackersData"
            disable-pagination
            disable-sort
            :refresh-button="false"
            :title-t-path="$getTPath(TRANSLATION_GROUP_DASHBOARD_ADMIN, 'TOP_TRACKERS_HEADER')"
            :columns-options="columnOptionsTrackers"
          >
            <template #searchFilter>
              <VDropdownMenu
                name="top_trackers"
                :options="periodOptions"
                underlined
                style-type="dark"
                :value="trackersOption"
                @change="setTrackersOption"
              />
            </template>
          </VTable>
        </VLoader>
      </VCard>

      <VCard
        v-if="topsPlayersData"
        type="table"
      >
        <VLoader
          :loading="isLoading"
          mode="overlay"
        >
          <VTable
            :data="topsPlayersData"
            disable-pagination
            disable-sort
            :refresh-button="false"
            :title-t-path="$getTPath(TRANSLATION_GROUP_DASHBOARD_ADMIN, 'TOP_PLAYERS_HEADER')"
            :columns-options="columnOptionsPlayers"
          >
            <template #searchFilter>
              <VDropdownMenu
                name="top_players"
                :options="periodOptions"
                underlined
                style-type="dark"
                :value="playersOption"
                @change="setPlayersOption"
              />
            </template>
          </VTable>
        </VLoader>
      </VCard>
    </div>
  </div>
</template>
